import { z } from 'zod/v4';
export const LegalDocumentIdSchema = z.enum(['terms', 'privacy', 'accessibility']);
export const LegalVersionStatusSchema = z.enum(['placeholder', 'published']);
// What a version bump of a document MEANS, declared once per document so no
// surface hard-codes it: `notice-and-continued-use` (Terms — a material
// version produces a dismissible notice from its posting instant, and
// continued use after it takes effect is the acceptance), `publication`
// (Privacy — published and rendered, no notice gate and no acknowledgement),
// `informational` (Accessibility — never recorded).
export const LegalAcceptanceModelSchema = z.enum([
    'notice-and-continued-use',
    'publication',
    'informational',
]);
// Fixed-precision UTC instant (`YYYY-MM-DDTHH:mm:ss.sssZ`). The current-version
// selector orders effective dates with a plain string compare, which is only
// well-ordered when every value shares one precision — `...:00Z` and
// `...:00.000Z` name the same instant but sort differently, so a registry that
// admitted both shapes could serve a stale version on an effective-date tie.
export const LegalInstantSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/, 'Expected a UTC ISO-8601 instant at millisecond precision (YYYY-MM-DDTHH:mm:ss.sssZ)');
// Two distinct dates, neither derived from the other: `effectiveDate` is the
// instant a version begins to govern (what the current-version selector reads)
// and `publishedAt` is the instant its text is posted (what the notice reads).
// They are separate facts because a version can be posted before it governs —
// the notice announces a still-future effective date.
export const LegalVersionSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    effectiveDate: LegalInstantSchema,
    publishedAt: LegalInstantSchema,
    material: z.boolean(),
    status: LegalVersionStatusSchema,
    body: z.string(),
});
// A document with no versions has no current version to serve or accept —
// `currentVersionOf` would return undefined and every downstream consumer
// (renderer, acceptance record) would silently render/handle nothing, so the
// shape itself is invalid.
export const LegalDocumentSchema = z.object({
    id: LegalDocumentIdSchema,
    title: z.string(),
    acceptanceModel: LegalAcceptanceModelSchema,
    versions: z.array(LegalVersionSchema).min(1),
});
// The pair a registration/re-acceptance records for an account. Accessibility
// is deliberately absent — it is informational and never recorded (the plan's
// "records terms and privacy versions only").
export const LegalAcceptanceSchema = z.object({
    termsVersion: z.number().int().positive(),
    privacyVersion: z.number().int().positive(),
});
// The Terms notice verdict — the single shared answer to "does this account
// owe a Terms notice right now", read by both the acceptance-state response and
// the portal. `due` is the decision; `version` and `effectiveDate` name the
// announced version (its number, and the instant it begins to govern) and are
// explicitly null exactly when no notice is due, so a reader never has to
// distinguish "absent" from "none".
export const TermsNoticeVerdictSchema = z.object({
    due: z.boolean(),
    version: z.number().int().nullable(),
    effectiveDate: LegalInstantSchema.nullable(),
});
// The account's recorded dismissal of a Terms notice — the version dismissed
// and the instant it was dismissed, both explicit nulls when none is recorded.
// Dismissing records evidence that notice was given; it locks nothing out.
export const DismissedNoticeSchema = z.object({
    version: z.number().int().nullable(),
    dismissedAt: z.string().nullable(),
});
// GET /api/legal/acceptance — the caller's recorded acceptance and the notice
// model's two legs. Timestamps are the account row's `..._accepted_at` values as
// ISO strings, null when that document was never accepted. `notice` and
// `dismissedNotice` are REQUIRED and always present with explicit nulls — the
// same always-present vocabulary `accepted` uses — so no reader distinguishes
// "absent" from "none".
export const LegalAcceptanceStateSchema = z.object({
    accepted: z.object({
        termsVersion: z.number().int().nullable(),
        termsAcceptedAt: z.string().nullable(),
        privacyVersion: z.number().int().nullable(),
        privacyAcceptedAt: z.string().nullable(),
    }),
    current: z.object({
        termsVersion: z.number().int().nullable(),
        privacyVersion: z.number().int().nullable(),
    }),
    notice: TermsNoticeVerdictSchema,
    dismissedNotice: DismissedNoticeSchema,
});
